/* =============================================
   Mikutowski Engraving Configurator
   assets/engraving-configurator.js
   ============================================= */

(function () {
  'use strict';

  /* ── FONT SCALE ── */
  const FONT_SCALE = {
    'Great Vibes, cursive':      1.25,
    'Cormorant Garamond, serif': 1.08,
    'Sanchez, serif':            0.94,
    'Raleway, sans-serif':       1.00,
  };

  /* ── ZONE CONFIG ── */
  const ZONE_CONFIG = {
    lid: {
      maxLines: 3, maxChars: 200,
      POS: {
        middle: { x: 725, anchor: 'middle' },
        end:    { x: 1120, anchor: 'end' },
      },
      LID_TOP: 252, LID_BOTTOM: 590, LID_CENTRE: 415,
      MAX_WIDTH: 760,
      lineCount: 4,
    },
    tray: {
      maxLines: 2, maxChars: 120,
      POS: {
        middle: { x: 1045, anchor: 'middle' },
        end:    { x: 1810, anchor: 'end' },
        right:  { x: 1810, anchor: 'end' },
      },
      LID_TOP: 399, LID_BOTTOM: 528, LID_CENTRE: 440,
      MAX_WIDTH: 1580,
      lineCount: 2,
    },
  };

  function applyGeomOverrides(root) {
    const t = (root.dataset.trayGeom || '').split(',').map(Number);
    if (t.length >= 6 && !t.slice(0,6).some(isNaN)) {
      ZONE_CONFIG.tray.POS.middle.x = t[0];
      ZONE_CONFIG.tray.POS.end.x = t[1];
      ZONE_CONFIG.tray.LID_TOP = t[2];
      ZONE_CONFIG.tray.LID_BOTTOM = t[3];
      ZONE_CONFIG.tray.LID_CENTRE = t[4];
      ZONE_CONFIG.tray.MAX_WIDTH = t[5];
      ZONE_CONFIG.tray.POS.right = { x: t[1], anchor: 'end' };
      if (t[6]) { ZONE_CONFIG.tray.maxLines = t[6]; ZONE_CONFIG.tray.lineCount = t[6]; }
      ZONE_CONFIG.tray.TOP_ANCHOR = t[7] === 1;
    }

    const g = (root.dataset.lidGeom || '').split(',').map(Number);
    if (g.length === 6 && !g.some(isNaN)) {
      ZONE_CONFIG.lid.POS.middle.x = g[0];
      ZONE_CONFIG.lid.POS.end.x = g[1];
      ZONE_CONFIG.lid.LID_TOP = g[2];
      ZONE_CONFIG.lid.LID_BOTTOM = g[3];
      ZONE_CONFIG.lid.LID_CENTRE = g[4];
      ZONE_CONFIG.lid.MAX_WIDTH = g[5];
    }
  }

  /* ── FONT LABEL MAP (human-readable for order) ── */
  const FONT_LABELS = {
    'Raleway, sans-serif':       'Raleway',
    'Great Vibes, cursive':      'Cursive (Great Vibes)',
    'Cormorant Garamond, serif': 'Garamond',
    'Sanchez, serif':            'Sanchez',
  };

  const ALIGN_LABELS = {
    middle: 'Centred',
    end:    'Bottom right',
    right:  'Right',
  };

  /* ── LAYOUT ENGINE ── */
  function estimateWidth(text, fontSize, font) {
    const factor = font.includes('Great Vibes') ? 0.46
      : font.includes('Sanchez')   ? 0.58
      : font.includes('Cormorant') ? 0.52
      : 0.55;
    return text.length * fontSize * factor;
  }

  function wrapLine(text, fontSize, font, maxWidth) {
    if (!text.trim()) return [];
    if (estimateWidth(text, fontSize, font) <= maxWidth) return [text];
    const chunks = text.match(/\S+|\s+/g) || [];
    const lines = [];
    let cur = '';
    for (const chunk of chunks) {
      const test = cur + chunk;
      if (estimateWidth(test, fontSize, font) <= maxWidth || cur === '') {
        cur = test;
      } else {
        if (cur.trim()) lines.push(cur.trimEnd());
        cur = chunk;
      }
    }
    if (cur.trim()) lines.push(cur.trimEnd());
    return lines;
  }

  function computeLayout(zone, text, font, cfg) {
    if (!text.trim()) return { lines: [], sizes: [] };
    const userLines  = text.split('\n').slice(0, cfg.maxLines);
    const hasNewline = text.includes('\n');
    const first      = userLines[0] || '';

    const baseSizes = zone === 'lid'
      ? [72, 60, 50, 42, 36, 30, 26]
      : [90, 75, 62, 52, 44, 36, 30];
    const thresholds = [3, 6, 10, 16, 22, 30];

    let base = baseSizes[baseSizes.length - 1];
    for (let i = 0; i < thresholds.length; i++) {
      if (first.length <= thresholds[i]) { base = baseSizes[i]; break; }
    }
    base *= (FONT_SCALE[font] || 1);

    if (!hasNewline) {
      let wrapped = wrapLine(first, base, font, cfg.MAX_WIDTH);
      while (wrapped.length > cfg.maxLines && base > 20) {
        base -= 2;
        wrapped = wrapLine(first, base, font, cfg.MAX_WIDTH);
      }
      return {
        lines: wrapped.slice(0, cfg.maxLines + 1),
        sizes: wrapped.slice(0, cfg.maxLines + 1).map(() => base),
      };
    }

    const RATIOS = [1.00, 0.76, 0.62];
    const lines = [], sizes = [];
    userLines.forEach((line, i) => {
      const sz = Math.max(Math.round(base * (RATIOS[i] || 0.62)), 20);
      wrapLine(line, sz, font, cfg.MAX_WIDTH).forEach(w => { lines.push(w); sizes.push(sz); });
    });
    return {
      lines: lines.slice(0, cfg.maxLines + 1),
      sizes: sizes.slice(0, cfg.maxLines + 1),
    };
  }

  /* ── RENDER ── */
  function render(zone, state, sectionId) {
    const cfg = ZONE_CONFIG[zone];
    const hasText = state.text.trim().length > 0;
    const prefix  = `mkw-${zone}`;

    /* placeholder */
    const ph = document.getElementById(`${prefix}-ph-${sectionId}`);
    if (ph) ph.style.display = hasText ? 'none' : '';

    const { lines, sizes } = computeLayout(zone, state.text, state.font, cfg);
    const pos = cfg.POS[state.align] || cfg.POS.middle;

    let totalH = 0;
    sizes.forEach(s => totalH += s * 1.22);

    const isEnd = state.align === 'end';
    let y = isEnd
      ? cfg.LID_BOTTOM - totalH + (sizes[0] || 30)
      : cfg.TOP_ANCHOR
        ? cfg.LID_TOP + (sizes[0] || 30)
        : cfg.LID_CENTRE - totalH / 2 + (sizes[0] || 30);

    for (let i = 1; i <= cfg.lineCount; i++) {
      const el   = document.getElementById(`${prefix}-l${i}-${sectionId}`);
      if (!el) continue;
      const line = lines[i - 1] || '';
      const size = sizes[i - 1] || 1;

      el.textContent = line;
      el.setAttribute('x',            pos.x);
      el.setAttribute('y',            line ? Math.round(y) : 0);
      el.setAttribute('text-anchor',  pos.anchor);
      el.setAttribute('font-family',  state.font);
      el.setAttribute('font-weight',  state.weight);
      el.setAttribute('font-style',   state.style);
      el.setAttribute('font-size',    line ? size : 1);
      el.setAttribute('fill',         line ? 'rgba(56,48,31,0.88)' : 'rgba(56,48,31,0)');
      el.setAttribute('letter-spacing', state.font.includes('Great Vibes') ? '0' : '1');

      if (line) y += size * 1.22;
    }
  }

  /* ── SYNC HIDDEN INPUTS (free mode) ── */
  function syncProps(zoneStates, sectionId) {
    const set = (id, val) => {
      const el = document.getElementById(id);
      if (el) el.value = val;
    };
    /* font and position are only recorded for a zone that has text,
       so an order never lists styling for an engraving that isn't there */
    const lid = zoneStates.lid;
    if (lid) {
      const has = lid.text.trim() !== '';
      set(`mkw-prop-lid-text-${sectionId}`,  lid.text.replace(/\n/g, ' / '));
      set(`mkw-prop-lid-font-${sectionId}`,  has ? (FONT_LABELS[lid.font] || lid.font) : '');
      set(`mkw-prop-lid-pos-${sectionId}`,   has ? (ALIGN_LABELS[lid.align] || lid.align) : '');
    }
    const tray = zoneStates.tray;
    if (tray) {
      const has = tray.text.trim() !== '';
      set(`mkw-prop-tray-text-${sectionId}`,  tray.text.replace(/\n/g, ' / '));
      set(`mkw-prop-tray-font-${sectionId}`,  has ? (FONT_LABELS[tray.font] || tray.font) : '');
      set(`mkw-prop-tray-pos-${sectionId}`,   has ? (ALIGN_LABELS[tray.align] || tray.align) : '');
    }
    /* hidden production JSON */
    set(`mkw-prop-json-${sectionId}`, JSON.stringify({
      lid:  lid  ? { text: lid.text,  font: lid.font,  align: lid.align  } : null,
      tray: tray ? { text: tray.text, font: tray.font, align: tray.align } : null,
    }));
  }

  /* ── PAID MODE: intercept ATC, add items array ── */
  function initPaidMode(wrapper, zoneStates, sectionId) {
    const formId  = wrapper.dataset.productFormId;
    const form    = document.getElementById(formId);
    if (!form) return;

    const lidProductId   = wrapper.dataset.lidProductId;
    const trayProductId  = wrapper.dataset.trayProductId;
    const ringsProductId = wrapper.dataset.ringsProductId;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;
      const variantId = variantInput.value;

      const items = [{ id: parseInt(variantId), quantity: 1 }];

      /* lid */
      const lid = zoneStates.lid;
      if (lid && lid.text.trim() && lidProductId) {
        items.push({
          id: parseInt(lidProductId),
          quantity: 1,
          properties: {
            'Lid text':     lid.text.replace(/\n/g, ' / '),
            'Lid font':     FONT_LABELS[lid.font] || lid.font,
            'Lid position': ALIGN_LABELS[lid.align] || lid.align,
          }
        });
      }

      /* tray */
      const tray = zoneStates.tray;
      if (tray && tray.text.trim() && trayProductId) {
        items.push({
          id: parseInt(trayProductId),
          quantity: 1,
          properties: {
            'Tray text':     tray.text.replace(/\n/g, ' / '),
            'Tray font':     FONT_LABELS[tray.font] || tray.font,
            'Tray position': ALIGN_LABELS[tray.align] || tray.align,
          }
        });
      }

      /* ring rolls */
      const ringsCheck = document.getElementById(`mkw-rings-check-${sectionId}`);
      if (ringsCheck && ringsCheck.checked && ringsProductId) {
        items.push({ id: parseInt(ringsProductId), quantity: 1 });
      }

      /* submit to Shopify */
      const submitBtn = form.querySelector('[type="submit"][name="add"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('loading');
      }

      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items }),
      })
        .then(r => r.json())
        .then(() => {
          /* trigger Minimog's cart drawer open */
          document.dispatchEvent(new CustomEvent('minimog:cart:add', { bubbles: true }));
          /* fallback: reload if no drawer */
          if (!document.querySelector('.m-cart-drawer')) {
            window.location.href = '/cart';
          }
        })
        .catch(err => {
          console.error('Engraving ATC error', err);
          alert('Something went wrong adding to cart. Please try again.');
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('loading');
          }
        });
    });
  }

  /* ── INIT PER CONFIGURATOR ── */
  /* ── Link the engraving fields to the product's add-to-cart form ──
     The snippet expects product_form_id from the caller; when it is blank the
     hidden fields point at nothing and the engraving never reaches the order.
     Find the product form ourselves and bind the fields to it. */
  function resolveProductForm(wrapper, sectionId) {
    let form = null;
    const given = wrapper.dataset.productFormId;
    if (given) form = document.getElementById(given);
    if (!form || form.tagName !== 'FORM') {
      const isProductForm = f => f.querySelector('[name="id"]') && f.querySelector('[type="submit"][name="add"], [name="add"]');
      const section = wrapper.closest('[id^="shopify-section"]');
      const pools = [
        section ? section.querySelectorAll('form[action*="/cart/add"]') : [],
        document.querySelectorAll('form[action*="/cart/add"][id^="product-form"]'),
        document.querySelectorAll('form[action*="/cart/add"]')
      ];
      for (const pool of pools) {
        form = Array.from(pool).find(isProductForm) || null;
        if (form) break;
      }
    }
    if (!form) return null;
    let id = form.getAttribute('id');
    if (!id) { id = 'mkw-product-form-' + sectionId; form.setAttribute('id', id); }
    wrapper.dataset.productFormId = id;
    wrapper.querySelectorAll('input[name^="properties["]').forEach(inp => inp.setAttribute('form', id));
    return form;
  }

  /* ── Ring rolls are a paid extra even when engraving is free ──
     When ticked, add the box (with its engraving properties) and the ring
     rolls together, then go to the cart. Unticked: the theme's normal
     add-to-cart runs untouched. */
  function initFreeRings(wrapper, sectionId, form) {
    const ringsId = wrapper.dataset.ringsProductId;
    if (!form || !ringsId || wrapper.dataset.hasRings !== 'true') return;
    let busy = false;
    const handler = function (e) {
      /* the main form's button, or the sticky add-to-cart bar's button */
      let trigForm = null, trigBtn = null;
      if (e.type === 'submit') {
        trigForm = e.target;
      } else {
        trigBtn = e.target.closest ? e.target.closest('[name="add"]') : null;
        if (!trigBtn) return;
        trigForm = trigBtn.form || trigBtn.closest('form');
      }
      if (!trigForm) return;
      const isSticky = (trigForm.getAttribute('id') || '').indexOf('sticky-atc-form') === 0;
      if (trigForm !== form && !isSticky) return;
      const check = document.getElementById(`mkw-rings-check-${sectionId}`);
      if (!check || !check.checked) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (busy) return;
      busy = true;
      const fd = new FormData(form);                       /* box + engraving live on the main form */
      const qfd = isSticky ? new FormData(trigForm) : fd;  /* the sticky bar has its own quantity */
      const variantId = parseInt(fd.get('id'), 10);
      const qty = parseInt(qfd.get('quantity') || fd.get('quantity') || '1', 10) || 1;
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && String(v).trim()) props[m[1]] = v;
      }
      const h1 = document.querySelector('h1');
      const items = [
        { id: variantId, quantity: qty, properties: props },
        { id: parseInt(ringsId, 10), quantity: qty, properties: { 'For': h1 ? h1.textContent.trim() : '' } }
      ];
      const btn = trigBtn || trigForm.querySelector('[name="add"]');
      if (btn) { btn.disabled = true; btn.classList.add('loading'); }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      })
        .then(r => r.ok ? r.json() : r.json().then(j => { throw new Error(j.description || j.message || 'Add to cart failed'); }))
        .then(() => { window.location.href = '/cart'; })
        .catch(err => {
          console.error('Ring rolls ATC error', err);
          alert(err.message || 'Something went wrong adding to cart. Please try again.');
          busy = false;
          if (btn) { btn.disabled = false; btn.classList.remove('loading'); }
        });
    };
    document.addEventListener('click', handler, true);
    document.addEventListener('submit', handler, true);
  }

  function initConfigurator(wrapper) {
    applyGeomOverrides(wrapper);
    const sectionId = wrapper.dataset.sectionId;
    const freeMode  = wrapper.dataset.freeMode === 'true';
    const hasTray   = wrapper.dataset.hasTray   === 'true';

    /* zone state */
    const zoneStates = {
      lid: {
        text: '', font: 'Raleway, sans-serif', weight: '400', style: 'normal', align: 'middle',
      },
    };
    if (hasTray) {
      zoneStates.tray = {
        text: '', font: 'Raleway, sans-serif', weight: '400', style: 'normal', align: 'middle',
      };
    }

    /* initial render */
    Object.keys(zoneStates).forEach(z => render(z, zoneStates[z], sectionId));

    /* ── textarea input ── */
    wrapper.querySelectorAll('.mkw-eng__textarea').forEach(ta => {
      const zone = ta.dataset.zone;
      if (!zoneStates[zone]) return;
      const cfg = ZONE_CONFIG[zone];

      ta.addEventListener('input', () => {
        if (ta.value.length > cfg.maxChars) ta.value = ta.value.slice(0, cfg.maxChars);
        zoneStates[zone].text = ta.value;

        const c = ta.value.length;
        const counter = document.getElementById(`mkw-${zone}-count-${sectionId}`);
        if (counter) {
          counter.textContent = `${c} / ${cfg.maxChars}`;
          counter.classList.toggle('is-warn', c > Math.floor(cfg.maxChars * 0.85));
        }
        const clearBtn = document.getElementById(`mkw-${zone}-clear-${sectionId}`);
        if (clearBtn) clearBtn.style.display = ta.value ? '' : 'none';

        render(zone, zoneStates[zone], sectionId);
        if (freeMode) syncProps(zoneStates, sectionId);
      });

      ta.addEventListener('keydown', e => {
        if (e.key === 'Enter' && ta.value.split('\n').length >= cfg.maxLines) {
          e.preventDefault();
        }
      });

      ta.addEventListener('paste', e => {
        const pasted = (e.clipboardData || window.clipboardData).getData('text');
        const start  = ta.selectionStart, end = ta.selectionEnd;
        const next   = ta.value.slice(0, start) + pasted + ta.value.slice(end);
        if (next.length <= cfg.maxChars) return;
        e.preventDefault();
        const room = cfg.maxChars - (ta.value.length - (end - start));
        if (room <= 0) return;
        const insert = pasted.slice(0, room);
        ta.value = ta.value.slice(0, start) + insert + ta.value.slice(end);
        ta.selectionStart = ta.selectionEnd = start + insert.length;
        ta.dispatchEvent(new Event('input'));
      });
    });

    /* ── chips ── */
    wrapper.querySelectorAll('.mkw-eng__chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const zone = chip.dataset.zone;
        if (!zoneStates[zone]) return;
        const cfg = ZONE_CONFIG[zone];

        let text = (chip.dataset.text || '')
          .replace(/&#10;/g, '\n')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#9825;/g, '♡')
          .slice(0, cfg.maxChars);

        const ta = document.getElementById(`mkw-${zone}-textarea-${sectionId}`);
        if (!ta) return;
        ta.value = text;
        zoneStates[zone].text = text;

        const c = text.length;
        const counter = document.getElementById(`mkw-${zone}-count-${sectionId}`);
        if (counter) {
          counter.textContent = `${c} / ${cfg.maxChars}`;
          counter.classList.toggle('is-warn', c > Math.floor(cfg.maxChars * 0.85));
        }
        const clearBtn = document.getElementById(`mkw-${zone}-clear-${sectionId}`);
        if (clearBtn) clearBtn.style.display = text ? '' : 'none';

        render(zone, zoneStates[zone], sectionId);
        if (freeMode) syncProps(zoneStates, sectionId);
      });
    });

    /* ── clear buttons ── */
    wrapper.querySelectorAll('.mkw-eng__clear').forEach(btn => {
      btn.addEventListener('click', () => {
        const zone = btn.dataset.zone;
        if (!zoneStates[zone]) return;
        const cfg = ZONE_CONFIG[zone];

        zoneStates[zone].text = '';
        const ta = document.getElementById(`mkw-${zone}-textarea-${sectionId}`);
        if (ta) ta.value = '';
        const counter = document.getElementById(`mkw-${zone}-count-${sectionId}`);
        if (counter) { counter.textContent = `0 / ${cfg.maxChars}`; counter.classList.remove('is-warn'); }
        btn.style.display = 'none';

        render(zone, zoneStates[zone], sectionId);
        if (freeMode) syncProps(zoneStates, sectionId);
      });
    });

    /* ── font buttons ── */
    wrapper.querySelectorAll('.mkw-eng__font-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const zone = btn.dataset.zone;
        if (!zoneStates[zone]) return;

        zoneStates[zone].font   = btn.dataset.font;
        zoneStates[zone].weight = btn.dataset.weight || '400';
        zoneStates[zone].style  = btn.dataset.style  || 'normal';

        const strip = document.getElementById(`mkw-${zone}-fonts-${sectionId}`);
        if (strip) strip.querySelectorAll('.mkw-eng__font-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        render(zone, zoneStates[zone], sectionId);
        if (freeMode) syncProps(zoneStates, sectionId);
      });
    });

    /* ── align buttons ── */
    wrapper.querySelectorAll('.mkw-eng__align-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const zone = btn.dataset.zone;
        if (!zoneStates[zone]) return;

        zoneStates[zone].align = btn.dataset.align;

        const strip = document.getElementById(`mkw-${zone}-aligns-${sectionId}`);
        if (strip) strip.querySelectorAll('.mkw-eng__align-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        render(zone, zoneStates[zone], sectionId);
        if (freeMode) syncProps(zoneStates, sectionId);
      });
    });

    /* ── bind engraving fields to the product form, then set up add-to-cart ── */
    const productForm = resolveProductForm(wrapper, sectionId);
    if (!freeMode) {
      initPaidMode(wrapper, zoneStates, sectionId);
    } else {
      initFreeRings(wrapper, sectionId, productForm);
    }
  }

  /* ── BOOT ── */
  function boot() {
    document.querySelectorAll('.mkw-engraving').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();